import { useMemo } from "react";

import {
  NAV_GROUP_NAMES,
  NAV_V3_ITEMS,
  NAV_V3_ITEMS_PRIMARY,
  REWARDS_NAV_ITEM,
  TOP_CALLS_NAV_ITEM,
} from "@/components/navigation/primary/constants";
import { PrimaryNavigationItemCollapsableWithIconI } from "@/components/navigation/primary/interfaces";
import Router from "@/helpers/router";
import { useFetchTopCallsSwitch } from "@/modules/top-calls/data/queries";
import { useFetchRewardsSwitch } from "@/modules/rewards/data/queries";

/**
 * The primary items with Rewards inside Performance, right after Leaderboards
 * (at the end of the group if Leaderboards isn't there).
 */
export const withRewardsNavItem = (
  items: Array<PrimaryNavigationItemCollapsableWithIconI>
): Array<PrimaryNavigationItemCollapsableWithIconI> =>
  items.map((item) => {
    if (item !== NAV_V3_ITEMS[NAV_GROUP_NAMES.PERFORMANCE] || !item.children) {
      return item;
    }

    const children = [...item.children];
    const leaderboardIndex = children.findIndex(
      (child) => child.href === Router.leaderboard()
    );

    if (leaderboardIndex === -1) {
      children.push(REWARDS_NAV_ITEM);
    } else {
      children.splice(leaderboardIndex + 1, 0, REWARDS_NAV_ITEM);
    }

    return { ...item, children };
  });

export const withTopCallsNavItem = (
  items: Array<PrimaryNavigationItemCollapsableWithIconI>
): Array<PrimaryNavigationItemCollapsableWithIconI> =>
  items.map((item) => {
    if (
      item.id !== NAV_V3_ITEMS[NAV_GROUP_NAMES.PERFORMANCE].id ||
      !item.children
    ) {
      return item;
    }

    const children = [...item.children];
    const rewardsIndex = children.findIndex(
      (child) => child.href === Router.rewards()
    );
    const afterIndex =
      rewardsIndex >= 0
        ? rewardsIndex
        : children.findIndex((child) => child.href === Router.leaderboard());
    children.splice(
      afterIndex >= 0 ? afterIndex + 1 : children.length,
      0,
      TOP_CALLS_NAV_ITEM
    );

    return { ...item, children };
  });

export const usePrimaryNavItems =
  (): Array<PrimaryNavigationItemCollapsableWithIconI> => {
    const { data: isRewardsOn } = useFetchRewardsSwitch();
    const { data: isTopCallsOn } = useFetchTopCallsSwitch();

    return useMemo(() => {
      const items =
        isRewardsOn === true
          ? withRewardsNavItem(NAV_V3_ITEMS_PRIMARY)
          : NAV_V3_ITEMS_PRIMARY;

      return isTopCallsOn === true ? withTopCallsNavItem(items) : items;
    }, [isRewardsOn, isTopCallsOn]);
  };
